/**
 * SeriesThumbnailGrid - Single column list of draggable series thumbnails
 * Displays thumbnails in a compact vertical layout
 */

import React, { useState } from "react";
import { useAtomValue } from "jotai";

import { SeriesThumbnail } from "./SeriesThumbnail";
import { SeriesThumbnail as ThumbnailData } from "../../hooks/useSeriesThumbnails";
import { SeriesReformatMenu } from "./SeriesReformatMenu";
import type { SeriesMetadata } from "../../utils/dicomMetadataLoader";
import { derivePlane, reformatBlockerFor } from "../../utils/seriesClassification";
import type { PlaneOrientation } from "../../viewport/types";
import { viewerSurfacesAtom } from "../../atoms/viewerSurfacesAtom";
import { emit as busEmit } from "../../lib/viewerBus";
import { logger } from "@/utils/logger";

interface SeriesThumbnailGridProps {
  thumbnails: ThumbnailData[];
  assignedSeriesIds?: Set<string>;
  onSeriesClick?: (seriesId: string) => void;
  onRetry?: (seriesId: string) => void;
  isLoading?: boolean; // Show skeleton placeholders while loading
  /** Series metadata for reformat eligibility. Omitted → no reformat menu. */
  seriesMetadata?: Record<string, SeriesMetadata>;
}

// Skeleton placeholder for loading state
const SkeletonThumbnail: React.FC = () => (
  <div className="flex items-center gap-2 p-1.5 rounded bg-bg-2/50 animate-pulse">
    <div className="w-12 h-12 bg-surface-2 rounded shrink-0" />
    <div className="flex-1 space-y-1.5">
      <div className="h-2.5 bg-surface-2 rounded w-16" />
      <div className="h-2 bg-surface-2 rounded w-10" />
    </div>
  </div>
);

export const SeriesThumbnailGrid: React.FC<SeriesThumbnailGridProps> = ({
  thumbnails,
  assignedSeriesIds,
  onSeriesClick,
  onRetry,
  isLoading = false,
  seriesMetadata,
}) => {
  const [menu, setMenu] = useState<{ seriesId: string; anchor: { x: number; y: number } } | null>(
    null,
  );
  const surfaces = useAtomValue(viewerSurfacesAtom);
  // The segmentation layout mounts native cor/sag panes that nnInteractive
  // seeds, and a reslice pane disables segmentation on its plane — so reformats
  // are not offered there. Matches the guard in resolveGridReslice.
  const reformatAvailable = !!seriesMetadata && !surfaces?.includes("labeling-seg");

  // Emit the intent; HangingProtocolGrid owns the destination slot, the
  // acquisition-group guard, and the assignment. The sidebar deliberately does
  // not know which pane a plane maps to. When no grid is mounted (series
  // picker, slice-dependent layout, single-viewport fallback) nothing
  // subscribes — say so instead of dropping the click on the floor.
  const requestReformat = (seriesId: string, plane: PlaneOrientation) => {
    if (busEmit("seriesReformatRequest", { seriesId, plane }) === 0) {
      busEmit("toast", { message: "Reformats aren't available in this view." });
      logger.warn("[SeriesThumbnailGrid] reformat request had no listener", { seriesId, plane });
    }
  };

  // Show skeleton placeholders while loading
  if (isLoading && thumbnails.length === 0) {
    return (
      <div className="px-2 py-2">
        <div className="sidebar-text-label mb-2 text-center">Series</div>
        <div className="flex flex-col gap-1.5">
          {[1, 2, 3, 4].map((i) => (
            <SkeletonThumbnail key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (thumbnails.length === 0) {
    return (
      <div className="px-2.5 py-4 text-center">
        <div className="text-xs sidebar-text-muted">No series available</div>
      </div>
    );
  }

  return (
    <div className="px-2 py-2">
      <div className="sidebar-text-label mb-2 text-center">Series</div>
      {/* Single column list */}
      <div className="flex flex-col gap-1.5">
        {thumbnails.map((thumbnail) => (
          <SeriesThumbnail
            key={thumbnail.seriesId}
            thumbnail={thumbnail}
            isAssigned={assignedSeriesIds?.has(thumbnail.seriesId) ?? false}
            onClick={onSeriesClick ? () => onSeriesClick(thumbnail.seriesId) : undefined}
            onRetry={onRetry ? () => onRetry(thumbnail.seriesId) : undefined}
            onContextMenu={
              reformatAvailable
                ? (e) => {
                    e.preventDefault();
                    setMenu({
                      seriesId: thumbnail.seriesId,
                      anchor: { x: e.clientX, y: e.clientY },
                    });
                  }
                : undefined
            }
          />
        ))}
      </div>
      <div className="mt-2 px-1">
        <p className="text-[8px] sidebar-text-muted text-center opacity-60">
          Drag to viewport to assign
        </p>
      </div>
      {menu && seriesMetadata?.[menu.seriesId] && (
        <SeriesReformatMenu
          anchor={menu.anchor}
          seriesDescription={seriesMetadata[menu.seriesId].series_description}
          blocker={reformatBlockerFor(seriesMetadata[menu.seriesId])}
          sourcePlane={derivePlane(seriesMetadata[menu.seriesId])}
          onGenerate={(plane) => requestReformat(menu.seriesId, plane)}
          onClose={() => setMenu(null)}
        />
      )}
    </div>
  );
};

export default SeriesThumbnailGrid;
